/**
 * Clickable Home stats (UI polish, 18 Oct 2026). Every count on a Home widget links to the list it counts, with the
 * filter in the URL so the list shows exactly that many rows. The widget and the list share the predicates and query
 * parameters below, so the two can't drift apart. Links from Home also carry `?from=home` (OriginLink) for "Back to Home".
 */
import type { Conversation } from '@tradeoutlet/shared';

const href = (path: string, params: Record<string, string | number | boolean | undefined | null> = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '' && v !== false) q.set(k, String(v));
  const s = q.toString();
  return s ? `${path}?${s}` : path;
};

// ---- Chat ----
export type ChatFilter = 'all' | 'unread' | 'mentions';
export const CHAT_FILTERS: ChatFilter[] = ['all', 'unread', 'mentions'];
/** Unread as Home and the sidebar count it: new messages in a conversation that isn't muted. */
export const isUnreadConversation = (c: Pick<Conversation, 'unread_count' | 'notify_level'>) => c.unread_count > 0 && c.notify_level !== 'none';
export const hasUnreadMentions = (c: Pick<Conversation, 'mention_count'>) => c.mention_count > 0;
export function chatFilterOf(c: Pick<Conversation, 'unread_count' | 'notify_level' | 'mention_count'>, f: ChatFilter) {
  return f === 'unread' ? isUnreadConversation(c) : f === 'mentions' ? hasUnreadMentions(c) : true;
}
export const parseChatFilter = (v: string | null): ChatFilter => (CHAT_FILTERS.includes(v as ChatFilter) ? v as ChatFilter : 'all');
export const chatHref = (filter: ChatFilter) => href('/chat', { filter: filter === 'all' ? undefined : filter });

// ---- Tasks ----
export type TaskDue = 'any' | 'overdue' | 'today';
export const TASK_DUES: TaskDue[] = ['any', 'overdue', 'today'];
export const parseTaskDue = (v: string | null): TaskDue => (TASK_DUES.includes(v as TaskDue) ? v as TaskDue : 'any');
export function taskDueOf(t: { due_date?: string | null; completed_at?: string | null }, due: TaskDue, today: string) {
  if (due === 'any') return true;
  if (!t.due_date || t.completed_at) return false;
  return due === 'overdue' ? t.due_date < today : t.due_date === today;
}
/** The Tasks list's default view: open tasks for me, every project. Home counts with exactly this filter. */
export const MY_OPEN_TASKS = { scope: 'mine', status: 'open', project: 'all' } as const;
export const tasksHref = (due: TaskDue = 'any') => href('/tasks', { due: due === 'any' ? undefined : due });

// ---- Issues ----
export interface IssueQuery { scope: 'mine' | 'all'; status: string; breached?: boolean }
/** The same parameters the Issues list sends for these URL filters (see IncidentsPage). */
export const issuesHref = (q: IssueQuery) => href('/incidents', { scope: q.scope, status: q.status, breached: q.breached ? 1 : undefined });

// ---- Hours ----
/** `start`/`end`, not `from`/`to`: `from` is taken by `?from=home`. */
export const hoursHref = (from: string, to: string) => href('/workforce/reports/hours', { start: from, end: to });

// ---- Other lists ----
export const acksHref = () => '/acknowledgements';
/** Checklists to do: the Checklists page's To do tab counts the same runs. */
export const isChecklistDue = (r: { status: string }) => r.status === 'open' || r.status === 'in_progress';
export const checklistsDueHref = () => '/checklists?tab=todo';
export const myShiftsHref = () => '/workforce/my-shifts';
export const timesheetsHref = () => '/workforce/timesheets';

/** "View 4 open issues": an accessible name for a stat link (UK English, singular/plural). */
export function statLabel(n: number | string, one: string, many = `${one}s`) {
  const k = typeof n === 'number' ? n : Number(n);
  return `View ${n} ${k === 1 ? one : many}`;
}

// ---- Buildings ----
/** The Tasks list a building's "N open tasks" opens: everything open there that you can see (same rule as the count). */
export const buildingTasksHref = (locationId: string) => href('/tasks', { scope: 'all', location_id: locationId, from: 'building' });
/** Issues at a building, matching the Issues card's counts. */
export const buildingIssuesHref = (locationId: string, o: { scope?: 'all' | 'mine'; severity?: string; breached?: boolean } = {}) =>
  href('/incidents', { scope: o.scope ?? 'all', status: 'open', location_id: locationId, severity: o.severity, breached: o.breached ? 1 : undefined, from: 'building' });
